/**
 * Piezas chicas compartidas por las pantallas del Vault (F3a).
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyRound, ShieldAlert, ShieldCheck, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { getAreaBadgeStyle } from '@/lib/brand-colors';
import { fmtRelative } from '@/lib/relative-time';
import {
  CENSUS_DESTINO_LABEL,
  CENSUS_ESTADO_LABEL,
  CENSUS_QUESTIONS,
  VAULT_AREA_LABEL,
  VAULT_AUDIT_ACTION_LABEL,
  VAULT_GROUP_ROLE_LABEL,
  VAULT_PERMISSION_LABEL,
  parsePrincipal,
  type CensusDestino,
  type CensusEstado,
  type CensusQuestionKey,
  type VaultAuditAction,
  type VaultAuditEvent,
  type VaultGroupRole,
  type VaultPermission,
} from '../../../../shared/types/vault';

/**
 * Etiquetas de permisos, roles, acciones de auditoría y censo en el idioma de la
 * UI. Las constantes de shared/types/vault.ts quedan como fallback (y como texto
 * para el backend); auditoría 28/09: se mostraban siempre en español.
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook de UI junto a sus componentes (mismo criterio que fmtRelative)
export function useVaultLabels() {
  const { t } = useTranslation();
  // memo por `t` (cambia con el idioma): así entra en deps de useMemo sin recomputar en cada render
  return useMemo(() => ({
    perm: (p: VaultPermission) => t(`vault.labels.perm.${p}`, VAULT_PERMISSION_LABEL[p]),
    role: (r: VaultGroupRole) => t(`vault.labels.role.${r}`, VAULT_GROUP_ROLE_LABEL[r]),
    action: (a: VaultAuditAction | string) => t(`vault.labels.action.${a}`, VAULT_AUDIT_ACTION_LABEL[a as VaultAuditAction] ?? a),
    destino: (d: CensusDestino) => t(`vault.labels.destino.${d}`, CENSUS_DESTINO_LABEL[d]),
    estado: (e: CensusEstado) => t(`vault.labels.estado.${e}`, CENSUS_ESTADO_LABEL[e]),
    question: (k: CensusQuestionKey) => t(`vault.labels.question.${k}`, CENSUS_QUESTIONS.find((q) => q.key === k)?.text ?? k),
  }), [t]);
}

export function AreaBadge({ area }: { area?: string | null }) {
  if (!area) return <span className="text-muted-foreground">—</span>;
  const style = getAreaBadgeStyle(area);
  return (
    <span
      className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium"
      style={{ backgroundColor: style.bg, color: style.text, borderColor: style.border }}
    >
      {VAULT_AREA_LABEL[area] ?? area}
    </span>
  );
}

export function PermissionBadge({ permission }: { permission: VaultPermission | null | undefined }) {
  const L = useVaultLabels();
  const { t } = useTranslation();
  if (!permission) return <Badge variant="outline">{t('vault.perm.none', 'Sin acceso')}</Badge>;
  return <Badge variant={permission === 'owner' ? 'brand' : 'secondary'}>{L.perm(permission)}</Badge>;
}

export function MfaBadge({ hasTotp }: { hasTotp: boolean }) {
  const { t } = useTranslation();
  return hasTotp ? (
    <Badge variant="success" className="gap-1">
      <ShieldCheck className="h-3 w-3" />
      TOTP
    </Badge>
  ) : (
    <Badge variant="warning" className="gap-1">
      <ShieldAlert className="h-3 w-3" />
      {t('vault.mfa.none', 'Sin MFA')}
    </Badge>
  );
}

// fmtRelative vive en @/lib/relative-time (13/10/2026); se re-exporta para los consumidores del Vault.
// eslint-disable-next-line react-refresh/only-export-components -- re-export de un helper, no un componente
export { fmtRelative };

// eslint-disable-next-line react-refresh/only-export-components -- helper de UI junto a sus componentes; Fast Refresh solo pierde estado local en dev
export function principalLabel(principal: string, t: (k: string, d: string, o?: Record<string, unknown>) => string): { label: string; sub: string; icon: 'user' | 'group' } {
  const parsed = parsePrincipal(principal);
  if (!parsed) return { label: principal, sub: '', icon: 'user' };
  if (parsed.kind === 'user') return { label: parsed.email, sub: '', icon: 'user' };
  if (parsed.kind === 'area')
    return { label: t('vault.grants.area_group', 'Grupo · {{area}}', { area: VAULT_AREA_LABEL[parsed.group] ?? parsed.group }), sub: t('vault.grants.area_sync', 'sincronizado del community map'), icon: 'group' };
  return { label: t('vault.grants.adhoc_group', 'Grupo ad-hoc'), sub: parsed.groupId, icon: 'group' };
}

export function GrantsList({ grants }: { grants: Array<{ principal: string; permission: VaultPermission }> }) {
  const { t } = useTranslation();
  if (!grants.length) return <p className="text-sm text-muted-foreground">{t('vault.grants.empty', 'Nadie más tiene acceso.')}</p>;
  return (
    <ul className="divide-y">
      {grants.map((g) => {
        const p = principalLabel(g.principal, t);
        return (
          <li key={g.principal} className="flex items-center gap-3 py-2">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
              {p.icon === 'group' ? <Users className="h-4 w-4" /> : <KeyRound className="h-4 w-4" />}
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">{p.label}</div>
              {p.sub && <div className="truncate text-xs text-muted-foreground">{p.sub}</div>}
            </div>
            <PermissionBadge permission={g.permission} />
          </li>
        );
      })}
    </ul>
  );
}

export function ActivityList({ items, emptyLabel }: { items: VaultAuditEvent[]; emptyLabel: string }) {
  const L = useVaultLabels();
  const { i18n } = useTranslation();
  if (!items.length) return <p className="text-sm text-muted-foreground">{emptyLabel}</p>;
  return (
    <ul className="space-y-3">
      {items.map((ev) => (
        <li key={ev.auditId} className="flex gap-3 text-sm">
          <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${ev.action === 'access_denied' || ev.action === 'locked_out' ? 'bg-rose-500' : 'bg-teal-400'}`} />
          <div className="min-w-0">
            <div>
              <span className="font-medium">{ev.actorEmail.split('@')[0]}</span>{' '}
              <span className="text-muted-foreground">{L.action(ev.action)}</span>
              {ev.secretName && <span className="text-muted-foreground"> · {ev.secretName}</span>}
            </div>
            <div className="text-xs text-muted-foreground">
              {fmtRelative(ev.ts, i18n.language)}
              {ev.ip ? ` · ${ev.ip}` : ''}
              {ev.detail ? ` · ${ev.detail}` : ''}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}
